import React, { useEffect, useState } from 'react';
import { Partner, CommonPartner } from 'types';
import { Chip, CircularProgress, Box } from '@mui/material';
import styles from './ShowPartnerSetting.module.scss';
import { getCommonPartners } from 'core/API/partners';

interface ShowPartnerSettingProps {
	partner: Partner;
}

const ShowPartnerSetting: React.FC<ShowPartnerSettingProps> = ({ partner }) => {
	const [commonPartner, setCommonPartner] = useState<CommonPartner | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchPartnerDetails = async () => {
			if (!partner?.partnerId) return;
			
			try {
				setLoading(true);
				const commonPartners = await getCommonPartners();
				const found = commonPartners.find(p => p.id === partner.partnerId);
				if (found) {
					setCommonPartner(found);
				}
			} catch (error) {
				console.error('Failed to fetch common partner details:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchPartnerDetails();
	}, [partner?.partnerId]);

	if (!partner) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}
	console.log('Rendering ShowPartnerSetting for partner:', partner);

	return (
		<div className={styles.showPartnerSetting}>
			<h3 className={styles.title}>{partner.label}</h3>
			<div className={styles.formContainer}>
				{/* Partner Information Section */}
				<div className={styles.section}>
					<h4 className={styles.sectionTitle}>Partner Information</h4>
					
					<div className={styles.fieldGroup}>
						<div className={styles.field}>
							<label className={styles.label}>Partner List ID</label>
							<span className={styles.value}>{partner.id}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Partner Label</label>
							<span className={styles.value}>{partner.label}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Partner Type</label>
							{loading ? (
								<CircularProgress size={16} />
							) : (
								<span className={styles.value}>{commonPartner?.label || partner.partnerId}</span>
							)}
						</div>

						{commonPartner?.description && (
							<div className={styles.field}>
								<label className={styles.label}>Partner Description</label>
								<span className={styles.value}>{commonPartner.description}</span>
							</div>
						)}

						<div className={styles.field}>
							<label className={styles.label}>Active Status</label>
							<Chip 
								label={partner.active ? 'Active' : 'Inactive'} 
								color={partner.active ? 'success' : 'default'}
								size="small"
							/>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Deleted Status</label>
							<Chip 
								label={partner.deleted ? 'Deleted' : 'Active'} 
								color={partner.deleted ? 'error' : 'success'}
								size="small"
							/>
						</div>

						{partner.created_at && (
							<div className={styles.field}>
								<label className={styles.label}>Created At</label>
								<span className={styles.value}>
									{new Date(partner.created_at).toLocaleString()}
								</span>
							</div>
						)}

						{partner.updated_at && (
							<div className={styles.field}>
								<label className={styles.label}>Last Updated</label>
								<span className={styles.value}>
									{new Date(partner.updated_at).toLocaleString()}
								</span>
							</div>
						)}
					</div>
				</div>

				{/* Workplaces Section */}
				{partner.workplaces && partner.workplaces.length > 0 && (
					<div className={styles.section}>
						<h4 className={styles.sectionTitle}>Assigned Workplaces</h4>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{partner.workplaces.map((workplace, index) => (
								<Chip
									key={index}
									label={workplace.split('/').pop() || workplace}
									color="primary"
									variant="outlined"
									size="small"
								/>
							))}
						</Box>
					</div>
				)}

				{/* Parameters Section */}
				{partner.parameters && (
					<div className={styles.section}>
						<h4 className={styles.sectionTitle}>Configuration Parameters</h4>
						<div className={styles.fieldGroup}>
							{(() => {
								// Handle parameters - could be object or array of objects
								let paramsToDisplay: Record<string, any> = {};

								if (Array.isArray(partner.parameters)) {
									// If it's an array, use the first element if it's an object
									if (partner.parameters.length > 0 && typeof partner.parameters[0] === 'object') {
										paramsToDisplay = partner.parameters[0];
									}
								} else if (typeof partner.parameters === 'object') {
									// If it's directly an object
									paramsToDisplay = partner.parameters;
								}

								// Display each parameter as a key-value pair
								return Object.keys(paramsToDisplay).map((key) => {
									const value = paramsToDisplay[key];
									
									// Mask sensitive values (tokens, passwords, secrets)
									const sensitiveKeys = ['token', 'password', 'secret', 'key', 'access_token', 'refresh_token', 'encrypted'];
									const isSensitive = sensitiveKeys.some(sensitiveKey => 
										key.toLowerCase().includes(sensitiveKey)
									);
									
									// Format the value
									let displayValue: string;
									if (isSensitive && typeof value === 'string' && value.length > 20) {
										// Show first 10 and last 10 characters for tokens
										displayValue = `${value.substring(0, 10)}...${value.substring(value.length - 10)}`;
									} else if (Array.isArray(value)) {
										displayValue = `[${value.length} items]`;
									} else if (typeof value === 'object' && value !== null) {
										displayValue = JSON.stringify(value, null, 2);
									} else if (value === null || value === undefined) {
										displayValue = 'N/A';
									} else {
										displayValue = String(value);
									}

									return (
										<div key={key} className={styles.field}>
											<label className={styles.label}>{key}</label>
											<span className={styles.value} style={{ fontFamily: isSensitive ? 'monospace' : 'inherit' }}>
												{displayValue}
											</span>
										</div>
									);
								});
							})()}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ShowPartnerSetting;
